import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  BarChart3,
  Sparkles,
  HelpCircle,
  Building2,
  TrendingUp,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { BrandMark, BrandWordmark } from "./LandingPage";
import SiteFootnote from "../components/SiteFootnote";
import EntityFooter from "../components/EntityFooter";
import Breadcrumb from "../components/Breadcrumb";
import {
  getBenchmarkCombo,
  getSiblingBenchmarks,
  getConceptAcrossIndustries,
  conceptSlugToRatioKey,
} from "../lib/industryConceptMap";
import {
  setStandardMeta,
  upsertJsonLd,
  buildArticleSchema,
  buildBreadcrumbSchema,
  buildFaqSchema,
  buildOrganizationSchema,
  ORG,
} from "../lib/seo";
import { api } from "../lib/api";

// Strip parenthetical suffixes from concept titles for benchmark headings.
// Concept titles like "Prime Cost (Restaurants)" and "Food Cost Percentage
// (Restaurants)" bake in an industry hint that's redundant when the page is
// already scoped to that industry.
function cleanTitle(title) {
  return String(title || "").replace(/\s*\([^)]*\)\s*$/, "").trim();
}

// Lowercase a concept title for prose, but preserve all-caps acronyms
// like DSCR, EBITDA, DSO, DIO, DPO. So "EBITDA Margin" -> "EBITDA margin"
// and "DSCR" -> "DSCR" (unchanged).
function loweredForProse(title) {
  return String(title)
    .split(/(\s+)/)
    .map((w) => (/^[A-Z0-9]{2,}$/.test(w) ? w : w.toLowerCase()))
    .join("");
}

/**
 * /benchmarks/:industry/:concept — long-tail SEO landing page.
 *
 * Every page combines:
 *   1. Curated industry benchmark range (from FOR_INDUSTRIES)
 *   2. Real peer median from /api/peer-stats (when n ≥ 5)
 *   3. Concept-level formula + why-it-matters (from LEARN_CONCEPTS)
 *   4. Industry-specific interpretation (why this metric matters HERE)
 *   5. Hand-derived People-Also-Ask block
 *   6. Cross-links to sibling metrics + same-concept-other-industries
 *   7. CTA to /analyze with the industry pre-filled
 *
 * Google can rank this on queries no competitor page can match, because
 * the (industry × concept × real peer data) triple doesn't exist elsewhere.
 */
export default function BenchmarkPage() {
  const { industry: industrySlug, concept: conceptSlug } = useParams();
  const combo = getBenchmarkCombo(industrySlug, conceptSlug);
  const [peer, setPeer] = useState({ loaded: false, medians: null, n: 0 });

  // Fetch real community median. Falls back gracefully when we don't yet
  // have n ≥ 5 contributions for the segment.
  useEffect(() => {
    if (!combo) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(
          `/peer-stats?industry=${encodeURIComponent(combo.industry.industryKey)}`
        );
        if (cancelled) return;
        const ratioKey = conceptSlugToRatioKey(combo.conceptSlug);
        const val = r?.data?.medians?.[ratioKey];
        setPeer({
          loaded: true,
          median: typeof val === "number" ? val : null,
          n: r?.data?.n || 0,
          minN: r?.data?.min_n || 5,
        });
      } catch {
        if (!cancelled) setPeer({ loaded: true, median: null, n: 0, minN: 5 });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [combo]);

  useEffect(() => {
    if (!combo) return;
    const url = `${window.location.origin}/benchmarks/${combo.industrySlug}/${combo.conceptSlug}`;
    const cleanConceptTitle = cleanTitle(combo.concept.title);
    const conceptLower = loweredForProse(cleanConceptTitle);
    const title = `${cleanConceptTitle} for ${combo.industry.sub} — Benchmark, Formula & What's Healthy | CFO Grade`;
    const desc = `What's a healthy ${conceptLower} for ${combo.industry.sub.toLowerCase()}? Industry benchmark: ${combo.industryTarget.value}. See how yours compares against real ${combo.industry.sub.toLowerCase()} peer data — free, no signup.`;
    setStandardMeta({ title, description: desc, url });

    const paa = derivePaa(combo);
    upsertJsonLd("benchmark-graph", {
      "@context": "https://schema.org",
      "@graph": [
        buildOrganizationSchema(),
        buildArticleSchema({ title, description: desc, url }),
        buildBreadcrumbSchema([
          { name: "Home", url: ORG.url },
          { name: "Benchmarks", url: `${ORG.url}/benchmarks` },
          { name: combo.industry.sub, url: `${ORG.url}/for/${combo.industrySlug}` },
          { name: cleanConceptTitle, url },
        ]),
        buildFaqSchema(paa),
      ],
    });

    return () => {
      const el = document.getElementById("benchmark-graph");
      if (el) el.remove();
    };
  }, [combo]);

  if (!combo) {
    return <Navigate to="/benchmarks" replace />;
  }

  const paa = derivePaa(combo);
  const siblings = getSiblingBenchmarks(combo.industrySlug, combo.conceptSlug);
  const otherIndustries = getConceptAcrossIndustries(
    combo.conceptSlug,
    combo.industrySlug
  );
  const conceptTitle = cleanTitle(combo.concept.title);
  const conceptTitleLower = loweredForProse(conceptTitle);
  const industryLower = combo.industry.sub.toLowerCase();
  const analyzeHref = `/analyze?industry=${encodeURIComponent(combo.industry.industryKey)}&utm_source=benchmark&utm_campaign=${encodeURIComponent(combo.industrySlug + "-" + combo.conceptSlug)}`;

  return (
    <div
      className="min-h-screen bg-[#F7F5F0] bg-grain"
      data-testid="benchmark-page"
    >
      <header className="max-w-3xl mx-auto px-6 sm:px-10 py-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <BrandWordmark size="sm" />
        </Link>
        <Link
          to="/benchmarks"
          className="text-sm text-[#5C665D] hover:text-[#1F382E] inline-flex items-center gap-1.5 transition-colors"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> All benchmarks
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 pt-6 pb-20">
        <Breadcrumb
          items={[
            { name: "Home", to: "/" },
            { name: "Benchmarks", to: "/benchmarks" },
            { name: combo.industry.sub, to: `/for/${combo.industrySlug}` },
            {
              name: combo.concept.title,
              to: `/benchmarks/${combo.industrySlug}/${combo.conceptSlug}`,
            },
          ]}
        />

        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#5C665D] bg-white border border-[#E5E0D8] rounded-full px-3 py-1.5 mt-4">
          <BarChart3 size={13} strokeWidth={1.5} /> Benchmark ·{" "}
          {combo.industry.sub}
        </div>

        <h1
          className="font-serif-display text-4xl sm:text-5xl text-[#1F382E] leading-[1.05] tracking-tight mt-5"
          data-testid="benchmark-title"
        >
          What&apos;s a healthy {conceptTitleLower} for{" "}
          <span className="italic text-[#C26D5C]">
            {industryLower}
          </span>
          ?
        </h1>

        {/* Answer-first card — the first thing humans AND crawlers see. */}
        <div
          className="mt-7 bg-white border-l-2 border-[#C26D5C] border-y border-r border-y-[#E5E0D8] border-r-[#E5E0D8] rounded-r-xl rounded-l-sm p-5 sm:p-6"
          data-testid="benchmark-answer-first"
        >
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#C26D5C]">
            Short answer
          </p>
          <p className="text-[16px] sm:text-[17px] text-[#1A1A1A] leading-relaxed mt-2">
            For {industryLower}, a healthy{" "}
            {conceptTitleLower} is around{" "}
            <span className="font-semibold text-[#1F382E]">
              {combo.industryTarget.value}
            </span>
            . {contextSentence(combo)}
          </p>
        </div>

        {/* Peer median hero — either real data (n≥5) or "coming as more owners join" */}
        <section
          className="mt-6 bg-[#1F382E]/5 border border-[#1F382E]/10 rounded-xl p-5 sm:p-6"
          data-testid="benchmark-peer-median"
        >
          <div className="flex items-start gap-3">
            <TrendingUp
              size={18}
              strokeWidth={1.5}
              className="text-[#C26D5C] mt-0.5 shrink-0"
            />
            <div className="flex-1">
              <p className="text-[11px] uppercase tracking-[0.22em] text-[#5C665D]">
                Real peer data
              </p>
              {!peer.loaded ? (
                <p className="text-sm text-[#5C665D] mt-2">
                  Loading community median…
                </p>
              ) : peer.median !== null ? (
                <>
                  <p className="text-[15px] text-[#1F382E] leading-relaxed mt-2">
                    <span className="font-semibold">
                      Median {conceptTitleLower} across{" "}
                      {peer.n} anonymous {industryLower}{" "}
                      submissions on CFO Grade:{" "}
                      <span className="text-[#C26D5C]">
                        {formatMedian(peer.median, combo.conceptSlug)}
                      </span>
                      .
                    </span>{" "}
                    Yours is graded against this benchmark plus the industry
                    range above.
                  </p>
                </>
              ) : (
                <p className="text-sm text-[#5C665D] leading-relaxed mt-2">
                  Community median publishes once we have {peer.minN || 5}+
                  anonymous submissions from {industryLower}.
                  Currently at {peer.n}. Run your numbers below to help build
                  it — your ratios are shared, your business name and dollar
                  amounts are not.
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Formula */}
        <section className="mt-10">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#5C665D]">
            Formula
          </p>
          <div className="mt-3 bg-white border border-[#E5E0D8] rounded-xl p-5 sm:p-6">
            <p className="font-mono text-[15px] text-[#1F382E] leading-relaxed">
              {combo.concept.formula}
            </p>
            <p className="text-sm text-[#5C665D] mt-3 leading-relaxed">
              {combo.concept.formulaPlain}
            </p>
          </div>
        </section>

        {/* Why it matters for THIS industry */}
        <section className="mt-10">
          <h2 className="font-serif-display text-2xl text-[#1F382E] tracking-tight">
            Why {conceptTitleLower} matters for{" "}
            {industryLower}
          </h2>
          <p className="text-[15px] text-[#1A1A1A] leading-relaxed mt-3">
            {combo.concept.why}
          </p>
          <p className="text-[15px] text-[#1A1A1A] leading-relaxed mt-4">
            {industrySpecificAngle(combo)}
          </p>
        </section>

        {/* Cross-industry snapshot — how does this metric look elsewhere? */}
        {otherIndustries.length > 0 && (
          <section
            className="mt-10"
            data-testid="benchmark-cross-industry"
          >
            <h2 className="font-serif-display text-2xl text-[#1F382E] tracking-tight">
              {conceptTitle} across other industries
            </h2>
            <div className="mt-4 space-y-2">
              {otherIndustries.map((row) => (
                <Link
                  key={row.industrySlug}
                  to={`/benchmarks/${row.industrySlug}/${combo.conceptSlug}`}
                  className="group flex items-center justify-between bg-white border border-[#E5E0D8] hover:border-[#1F382E]/30 rounded-lg px-4 py-3 transition-all"
                  data-testid={`benchmark-cross-${row.industrySlug}`}
                >
                  <span className="text-sm font-medium text-[#1F382E] group-hover:text-[#C26D5C] transition-colors">
                    {row.industry.sub}
                  </span>
                  <span className="text-sm font-semibold tabular-nums text-[#5C665D]">
                    {row.industryTarget.value}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* People-also-ask */}
        <section className="mt-12" data-testid="benchmark-paa">
          <div className="flex items-center gap-2">
            <HelpCircle
              size={16}
              strokeWidth={1.5}
              className="text-[#5C665D]"
            />
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#5C665D]">
              People also ask
            </p>
          </div>
          <h2 className="font-serif-display text-2xl text-[#1F382E] tracking-tight mt-2">
            Common questions about {conceptTitleLower} for{" "}
            {industryLower}
          </h2>
          <div className="mt-5 divide-y divide-[#E5E0D8] border-y border-[#E5E0D8]">
            {paa.map((item, i) => (
              <details
                key={i}
                className="group py-4"
                data-testid={`benchmark-paa-item-${i}`}
                {...(i === 0 ? { open: true } : {})}
              >
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4 text-[15px] font-medium text-[#1F382E] hover:text-[#C26D5C] transition-colors">
                  <span>{item.q}</span>
                  <span className="text-[#5C665D] group-open:rotate-45 transition-transform text-lg leading-none">
                    +
                  </span>
                </summary>
                <p className="text-[14.5px] text-[#1A1A1A] leading-relaxed mt-3 pr-6">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mt-12 bg-white border border-[#E5E0D8] rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <div>
            <p className="font-serif-display text-xl text-[#1F382E] tracking-tight">
              See your {conceptTitleLower} — graded against{" "}
              {industryLower} peers.
            </p>
            <p className="text-sm text-[#5C665D] mt-2 max-w-md">
              Paste your P&L. CFO Grade computes this — plus 23 other ratios —
              in seconds, with the {combo.industry.sub.toLowerCase()}{" "}
              benchmark already loaded.
            </p>
          </div>
          <Link to={analyzeHref}>
            <Button
              className="bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md h-11 px-6 transition-all duration-300"
              data-testid="benchmark-cta-analyze"
            >
              Run a free analysis{" "}
              <ArrowRight size={14} strokeWidth={1.5} className="ml-2" />
            </Button>
          </Link>
        </section>

        {/* Other benchmarks in this industry — deep crawl signal */}
        {siblings.length > 0 && (
          <section className="mt-14" data-testid="benchmark-siblings">
            <h2 className="font-serif-display text-2xl text-[#1F382E] tracking-tight">
              Other {combo.industry.sub.toLowerCase()} benchmarks
            </h2>
            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              {siblings.map((s) => (
                <Link
                  key={s.conceptSlug}
                  to={`/benchmarks/${combo.industrySlug}/${s.conceptSlug}`}
                  className="group flex items-center justify-between bg-white border border-[#E5E0D8] hover:border-[#1F382E]/30 rounded-xl px-4 py-3 transition-all"
                  data-testid={`benchmark-sibling-${s.conceptSlug}`}
                >
                  <div>
                    <p className="text-sm font-medium text-[#1F382E] group-hover:text-[#C26D5C] transition-colors">
                      {s.concept.title}
                    </p>
                    <p className="text-[12px] text-[#5C665D] mt-0.5">
                      Target: {s.industryTarget.value}
                    </p>
                  </div>
                  <ArrowRight
                    size={14}
                    strokeWidth={1.5}
                    className="text-[#5C665D] group-hover:translate-x-0.5 transition-transform"
                  />
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Cross-link to the industry hub */}
        <section
          className="mt-12 bg-[#1F382E]/4 border border-[#1F382E]/10 rounded-2xl p-6 sm:p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          data-testid="benchmark-industry-crosslink"
        >
          <div className="flex items-start gap-3">
            <Building2
              size={18}
              strokeWidth={1.5}
              className="text-[#C26D5C] mt-0.5 shrink-0"
            />
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#C26D5C] font-medium">
                All benchmarks for this industry
              </p>
              <p className="font-serif-display text-lg text-[#1F382E] tracking-tight leading-tight mt-1">
                {combo.industry.sub} financial health hub
              </p>
              <p className="text-sm text-[#5C665D] mt-1.5 max-w-md">
                Every metric that matters for{" "}
                {combo.industry.sub.toLowerCase()} — margins, coverage,
                efficiency — with plain-English explanations.
              </p>
            </div>
          </div>
          <Link
            to={`/for/${combo.industrySlug}`}
            className="text-sm font-medium text-[#1F382E] hover:text-[#C26D5C] inline-flex items-center gap-1.5 transition-colors whitespace-nowrap shrink-0"
          >
            Open hub <ArrowRight size={13} strokeWidth={1.5} />
          </Link>
        </section>

        <EntityFooter />
      </main>

      <SiteFootnote />
    </div>
  );
}

// -- helpers -----------------------------------------------------------------

// One-line contextual note tacked onto the "short answer" card. Picks a
// sensible sentence based on the concept's category so the answer feels
// tuned rather than generic.
function contextSentence(combo) {
  const cat = combo.concept.category;
  const ind = combo.industry.sub.toLowerCase();
  const operators = `operators in ${ind}`;
  const Operators = operators[0].toUpperCase() + operators.slice(1);
  if (cat === "Leverage")
    return `That's the threshold lenders and sureties underwriting ${ind} use as a pass/fail line.`;
  if (cat === "Liquidity")
    return `${Operators} that break this range usually break on cash flow, not revenue.`;
  if (cat === "Profitability")
    return `Below the low end and you're subsidising the customer; above the high end and you're likely mispricing.`;
  if (cat === "Efficiency")
    return `${Operators} outside this range typically have a working-capital drag that gets worse as they scale.`;
  return `It's the range most lenders, acquirers, and consultants use as the "healthy" band for ${ind}.`;
}

// A short industry-specific paragraph explaining WHY this metric matters
// for this specific industry, not just in the abstract. Uses the concept's
// category to produce a defensible default when we haven't hand-authored one.
function industrySpecificAngle(combo) {
  const ind = combo.industry.sub.toLowerCase();
  const title = loweredForProse(cleanTitle(combo.concept.title));
  const painPoint = (combo.industry.painPoints || [])[0];
  const cat = combo.concept.category;
  // Some industry sub names are singular ("Manufacturing", "Retail",
  // "Agriculture", "Healthcare practice") so pluralised sentences read
  // awkwardly. Use a neutral operator subject instead.
  const operators = `operators in ${ind}`;

  if (cat === "Leverage") {
    return `For ${ind}, ${title} is often the make-or-break number when applying for a loan, a line of credit, or (in construction) a bonding increase. ${painPoint || "Owners in this sector routinely underestimate how much slack their lender wants to see."}`;
  }
  if (cat === "Liquidity") {
    return `${operators[0].toUpperCase() + operators.slice(1)} tend to look profitable on paper while quietly running out of working capital. ${title} is one of the earliest signals your accounting can give you before it becomes a cash crisis.`;
  }
  if (cat === "Profitability") {
    return `${title[0].toUpperCase() + title.slice(1)} is where cost of goods, pricing, and operating leverage meet. For ${ind}, small changes here compound — a 2-point improvement typically doubles owner take-home over a year.`;
  }
  if (cat === "Efficiency") {
    return `${operators[0].toUpperCase() + operators.slice(1)} are especially sensitive to ${title} because tied-up cash directly limits your ability to reinvest, take on inventory, or hire. It's the metric that quietly caps growth.`;
  }
  return `Understanding your ${title} in the context of ${ind} lets you see whether you're mid-pack, exceptional, or at risk — before your bank or accountant tells you.`;
}

// Answer-first PAA block, derived from the combo. Keeps each page unique
// (different industry × different concept produces different Q&As) so
// Google doesn't see the pages as duplicates.
function derivePaa(combo) {
  const ind = combo.industry.sub.toLowerCase();
  const title = cleanTitle(combo.concept.title);
  const titleLower = loweredForProse(title);
  const target = combo.industryTarget.value;
  const cat = combo.concept.category;

  const commonThresholds = {
    Leverage:
      "Lenders in this sector treat this as the minimum acceptable ratio. Falling below it doesn't guarantee a rejection, but it does guarantee more scrutiny, higher pricing, and often a personal guarantee.",
    Liquidity:
      "Below this range, most operators start needing a line of credit just to make payroll or restock — and the interest on that quickly erodes what's left of your margin.",
    Profitability:
      "Below this range, you're typically not generating enough profit per dollar of revenue to reinvest in growth, pay owners fairly, or weather a bad quarter.",
    Efficiency:
      "Above this range, cash is tied up in receivables or inventory that isn't turning into revenue quickly enough to fund the next cycle.",
  };

  return [
    {
      q: `What is a good ${titleLower} for ${ind}?`,
      a: `For ${ind}, a healthy ${titleLower} is around ${target}. The exact number depends on scale, region, and business model, but this is the range most banks, acquirers, and industry consultants treat as "healthy."`,
    },
    {
      q: `How is ${title} calculated?`,
      a: combo.concept.formulaPlain || combo.concept.formula,
    },
    {
      q: `Why does ${titleLower} matter more for ${ind}?`,
      a:
        commonThresholds[cat] ||
        `${title} is one of the top metrics used to underwrite ${ind}. Lenders, buyers, and even landlords tend to check it before signing anything.`,
    },
    {
      q: `What's the fastest way to improve my ${titleLower}?`,
      a: `Run your numbers through CFO Grade — the free memo pinpoints the two or three levers that move ${titleLower} fastest for a business your size, in your industry. Common fixes for ${ind} include ${quickFixHint(combo)}.`,
    },
  ];
}

function quickFixHint(combo) {
  const cat = combo.concept.category;
  if (cat === "Leverage")
    return "either accelerating debt paydown or restructuring existing debt to lower annual service.";
  if (cat === "Liquidity")
    return "trimming inventory days, tightening AR collections, or negotiating longer payment terms with vendors.";
  if (cat === "Profitability")
    return "pricing reviews, menu / SKU rationalization, and cutting the bottom 15% of unprofitable products or customers.";
  if (cat === "Efficiency")
    return "moving to net-15 collections, tightening reorder cycles, or eliminating dead SKUs.";
  return "pricing, cost discipline, and cash-cycle tightening in that order.";
}

// Format the median value for display. Ratios like margins are percents;
// coverage ratios like DSCR are decimals with an 'x' suffix; day-counts
// are integers with " days" suffix. Heuristic based on the slug — good
// enough for a display line.
function formatMedian(v, slug) {
  if (v === null || v === undefined) return "—";
  if (
    slug === "dscr" ||
    slug === "debt-to-ebitda" ||
    slug === "interest-coverage" ||
    slug === "current-ratio" ||
    slug === "quick-ratio" ||
    slug === "inventory-turnover" ||
    slug === "receivables-turnover" ||
    slug === "payables-turnover" ||
    slug === "burn-multiple"
  ) {
    return `${v.toFixed(2)}×`;
  }
  if (
    slug === "dso" ||
    slug === "dio" ||
    slug === "dpo" ||
    slug === "cash-conversion-cycle" ||
    slug === "runway" ||
    slug === "payback-period"
  ) {
    return `${Math.round(v)} days`;
  }
  // Default: percent
  return `${v.toFixed(1)}%`;
}

// Keep the BrandMark import referenced in case a later redesign uses it —
// prevents a stray unused-import lint error.
void BrandMark;
